import { ChevronRight, LogOut, Moon, Sun } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useAuthStore } from '../../store/useAuthStore';
import { useThemeStore } from '../../store/useThemeStore';

export interface Crumb {
  label: string;
  href?: string;
}

export function UserMenu() {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  if (!user) return null;
  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-sky-500 to-cyan-400 text-sm font-semibold uppercase text-white shadow-sm"
        title={user.username}
      >
        {user.username.charAt(0)}
      </button>
      {open && (
        <div className="absolute right-0 top-full z-50 mt-2 w-60 rounded-xl border border-gray-200/80 bg-panel p-1 shadow-pop dark:border-white/10 dark:bg-gray-900">
          <div className="px-3 py-2">
            <div className="truncate text-sm font-semibold dark:text-gray-100">{user.username}</div>
            <div className="truncate text-xs text-gray-500 dark:text-gray-400">{user.email}</div>
          </div>
          <div className="my-1 h-px bg-gray-200 dark:bg-white/10" />
          <button
            onClick={() => logout()}
            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-white/10"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

export default function AppHeader({ crumbs = [] }: { crumbs?: Crumb[] }) {
  const theme = useThemeStore((s) => s.theme);
  const toggleTheme = useThemeStore((s) => s.toggleTheme);
  return (
    <header className="sticky top-0 z-30 border-b border-gray-200/70 bg-white/70 backdrop-blur-xl dark:border-white/10 dark:bg-gray-950/70">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-6">
        <nav className="flex min-w-0 items-center gap-2 text-sm">
          <a href="#/" className="flex shrink-0 items-center gap-2.5">
            <img src="/logo.svg" alt="" className="h-8 w-8 rounded-lg shadow-sm shadow-sky-600/30" />
            <span className="text-[15px] font-semibold tracking-tight dark:text-gray-100">System Studio</span>
          </a>
          {crumbs.map((c) => (
            <span key={c.label} className="flex min-w-0 items-center gap-2 text-gray-400 dark:text-gray-500">
              <ChevronRight className="h-4 w-4 shrink-0" />
              {c.href ? (
                <a href={c.href} className="truncate rounded-md px-1.5 py-0.5 text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/10">
                  {c.label}
                </a>
              ) : (
                <span className="truncate rounded-md bg-gray-100 px-2 py-0.5 font-medium text-gray-900 dark:bg-white/10 dark:text-gray-100">{c.label}</span>
              )}
            </span>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <button onClick={toggleTheme} className="btn-ghost !p-2" title="Toggle dark mode">
            {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          <UserMenu />
        </div>
      </div>
    </header>
  );
}
